import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStrava } from '../hooks/useStrava';
import { uploadDataToS3 } from '../services/s3Service';

import './Refresh.css';

const isS3Configured = (): boolean => {
  return !!(
    process.env.REACT_APP_AWS_ACCESS_KEY_ID &&
    process.env.REACT_APP_AWS_SECRET_ACCESS_KEY &&
    process.env.REACT_APP_AWS_REGION &&
    process.env.REACT_APP_S3_BUCKET_NAME
  );
};

const Refresh: FC = () => {
  const { login, isAuthenticated, athleteInfo, loadRunningStats, loadAthleteInfo } = useStrava();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState<string>('');

  const handleLogin = () => {
    login();
  };

  const handleCancel = () => {
    navigate('/');
  };

  const uploadData = async (data: { timestamp: string; athleteInfo: any; runningStats: any; year: number }) => {
    try {
      const fileName = `running-stats/${data.year}/strava_data.json`;
      const location = await uploadDataToS3(fileName, data);

      return `Data uploaded successfully to S3: ${location}`;
    } catch (error: any) {
      console.error('Error uploading data to S3:', error);
      throw error;
    }
  };

  const handleRefresh = async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsRefreshing(true);
    setRefreshStatus('Fetching fresh data from Strava...');

    try {
      await loadAthleteInfo();

      const currentYear = new Date().getFullYear();
      const runningStats = await loadRunningStats(currentYear);

      const dataToSave = {
        timestamp: new Date().toISOString(),
        athleteInfo,
        runningStats,
        year: currentYear,
      };

      setRefreshStatus('Uploading data to S3...');
      console.warn(dataToSave);
      const uploadMessage = await uploadData(dataToSave);

      setRefreshStatus(`Data refreshed successfully! ${uploadMessage}`);

      // navigate('/');
    } catch (error) {
      console.error('Error refreshing data:', error);
      setRefreshStatus('Error refreshing data. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="content">
        <main>
          <div>
            <h2>Refresh Strava Data</h2>
            <p>You are not currently logged in to Strava. Please log in to refresh your data.</p>
            <div className="refresh-button-container">
              <button onClick={handleLogin} className="refresh-button refresh-button-primary">
                Login to Strava
              </button>
              <button onClick={handleCancel} className="refresh-button refresh-button-secondary">
                Back to Home
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="content">
      <main>
        <div>
          <h2>Refresh Strava Data</h2>
          {athleteInfo && (
            <div className="refresh-athlete-info">
              <p>
                You are currently logged in as:{' '}
                <strong>
                  {athleteInfo.firstname} {athleteInfo.lastname}
                </strong>
              </p>
            </div>
          )}

          {refreshStatus && (
            <div className={`refresh-status ${isRefreshing ? 'refresh-status-loading' : 'refresh-status-success'}`}>
              {refreshStatus}
            </div>
          )}

          <p>
            Click the button below to fetch fresh data from Strava and{' '}
            {isS3Configured() ? 'upload it to your S3 bucket for persistent storage' : 'download it as a JSON file'}.
          </p>
          <div className="refresh-button-container">
            <button onClick={handleRefresh} disabled={isRefreshing} className="refresh-button refresh-button-primary">
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
            <button onClick={handleCancel} disabled={isRefreshing} className="refresh-button refresh-button-secondary">
              Cancel
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Refresh;
